import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import UserRemoveDialogIcon from "@entrilia/shared/icons/UserRemoveDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { ApiGatewaySubscription } from "../../../api/types/integrationsTypes";

interface Props {
  subscription: ApiGatewaySubscription;
  onCancel: () => void;
  onSubmit: (subscriptionId: string) => void;
}

const DeleteSubscriptionDialog = ({ subscription, onCancel, onSubmit }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [deleting, setDeleting] = useState(false);

  const handleCancel = () => {
    onCancel();
  };

  const handleSubmit = async () => {
    setDeleting(true);
    try {
      await adminApi.deleteApiGatewaySubscription(subscription.id);
      sendNotification(`Subscription for ${subscription.username} deleted`);
      onSubmit(subscription.id);
    } catch (error) {
      logError(error, "DeleteApiGatewaySubscriptionDialog");
      sendNotificationError(`Failed to delete subscription for ${subscription.username}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <SmallDialog open onClose={handleCancel}>
      <DialogTitle>Delete Subscription</DialogTitle>
      <DialogCloseButton onClick={handleCancel} />
      <DialogContent sx={(theme) => ({ px: theme.spacing(4) })}>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <UserRemoveDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1}>
            <Typography variant="h6">Are you sure you want to delete the subscription?</Typography>
            <Stack>
              <Typography variant="subtitle1" sx={{ textAlign: "center" }}>
                {subscription.username}
              </Typography>
              <Typography color="textSecondary" sx={{ textAlign: "center" }}>
                {subscription.email}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={handleCancel}>
        <Button variant="outlined" color="error" loading={deleting} onClick={handleSubmit}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteSubscriptionDialog;
